import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AdditionalNotesTemplate } from '@/types';
import { supabase } from '@/lib/supabase';
import { generatePrescriptionId } from '@/lib/utils/prescriptionId';

// Define the store state interface
interface AdditionalNotesTemplatesState {
  templates: AdditionalNotesTemplate[];
  
  // Actions
  addTemplate: (template: Omit<AdditionalNotesTemplate, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>) => string;
  updateTemplate: (template: AdditionalNotesTemplate) => void;
  removeTemplate: (id: string) => void;
  incrementUsage: (id: string) => void;
  clearTemplates: () => void;
  
  // Selectors
  getTemplatesByDoctor: (doctorId: string) => AdditionalNotesTemplate[];
  getTemplateById: (id: string) => AdditionalNotesTemplate | undefined;
  searchTemplates: (doctorId: string, searchTerm: string) => AdditionalNotesTemplate[];
  
  // Fetch templates from Supabase
  fetchTemplates: (doctorId: string) => Promise<void>;
}

// Create a function to get user-specific storage key
const getStorageKey = () => 'additional-notes-templates-storage';

export const useAdditionalNotesTemplatesStore = create<AdditionalNotesTemplatesState>()(
  persist(
    (set, get) => ({
      templates: [],
      
      // Add a new template
      addTemplate: (template) => {
        const newTemplate: AdditionalNotesTemplate = {
          ...template,
          id: generatePrescriptionId(get().templates),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          usageCount: 0
        };
        
        set(state => ({
          templates: [...state.templates, newTemplate]
        }));
        
        return newTemplate.id;
      },
      
      // Update an existing template
      updateTemplate: (template) => {
        set(state => ({
          templates: state.templates.map(t => 
            t.id === template.id 
              ? { ...template, updatedAt: new Date().toISOString() }
              : t
          )
        }));
      },
      
      // Remove a template
      removeTemplate: (id) => {
        set(state => ({
          templates: state.templates.filter(t => t.id !== id)
        }));
      },
      
      // Increment usage count
      incrementUsage: (id) => {
        set(state => ({
          templates: state.templates.map(t => 
            t.id === id 
              ? { 
                  ...t, 
                  usageCount: t.usageCount + 1, 
                  lastUsed: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              : t
          )
        }));
      },
      
      // Get templates by doctor
      getTemplatesByDoctor: (doctorId) => {
        return get().templates.filter(template => template.doctorId === doctorId);
      },
      
      // Get template by ID
      getTemplateById: (id) => {
        return get().templates.find(template => template.id === id);
      },
      
      // Search templates
      searchTemplates: (doctorId, searchTerm) => {
        const term = searchTerm.toLowerCase();
        return get().templates.filter(template => 
          template.doctorId === doctorId &&
          (template.disease.toLowerCase().includes(term) ||
           template.templateName.toLowerCase().includes(term) ||
           template.notes.toLowerCase().includes(term))
        );
      },
      
      // Fetch templates from Supabase
      fetchTemplates: async (doctorId) => {
        try {
          const { data, error } = await supabase
            .from('additional_notes_templates')
            .select('*')
            .eq('doctor_id', doctorId)
            .order('updated_at', { ascending: false });
            
          if (error) {
            // If table doesn't exist, return empty array instead of throwing
            if (error.code === 'PGRST205') {
              console.warn('Additional notes templates table not found. Please run the database migration first.');
              set({ templates: [] });
              return;
            }
            console.error('Error fetching additional notes templates:', error);
            throw error;
          }
          
          // Transform data to match our interface
          const transformedTemplates = data?.map((item: any) => ({
            id: item.id,
            doctorId: item.doctor_id,
            disease: item.disease,
            templateName: item.template_name,
            notes: item.notes,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
            usageCount: item.usage_count || 0,
            lastUsed: item.last_used
          })) || [];
          
          set({ templates: transformedTemplates });
        } catch (error) {
          console.error('Error in fetchTemplates:', error);
          // Don't throw error, just set empty templates
          set({ templates: [] });
        }
      },
      
      // Clear templates (useful for switching users)
      clearTemplates: () => {
        set({ templates: [] });
      },
    }),
    {
      name: getStorageKey(),
      skipHydration: true,
    }
  )
);

// Save template to backend
export async function saveAdditionalNotesTemplate(templateData: {
  doctorId: string;
  disease: string;
  templateName: string;
  notes: string;
}) {
  try {
    console.log('Saving additional notes template with data:', templateData);
    
    // Check if template with same disease and name already exists
    try {
      const { data: existingTemplate } = await supabase
        .from('additional_notes_templates')
        .select('id')
        .eq('doctor_id', templateData.doctorId)
        .eq('disease', templateData.disease)
        .eq('template_name', templateData.templateName)
        .single();

      if (existingTemplate) {
        throw new Error('A template with this disease and name already exists');
      }
    } catch (error) {
      // If duplicate check fails due to RLS policy, log and continue
      console.log('Duplicate check failed due to RLS policy, continuing with insert:', error);
    }

    // Generate ID for the template
    const { data: existingTemplates } = await supabase
      .from('additional_notes_templates')
      .select('id, created_at')
      .order('created_at', { ascending: false });

    const templateId = generatePrescriptionId(existingTemplates || []);
    console.log('Generated additional notes template ID:', templateId);

    // Create insert data
    const insertData = {
      id: templateId,
      doctor_id: templateData.doctorId,
      disease: templateData.disease,
      template_name: templateData.templateName,
      notes: templateData.notes,
      usage_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    console.log('Insert data:', insertData);

    const { data, error } = await supabase
      .from('additional_notes_templates')
      .insert([insertData]);

    if (error) {
      // If table doesn't exist, provide helpful error message
      if (error.code === 'PGRST205') {
        throw new Error('Additional notes templates table not found. Please run the database migration first.');
      }
      console.error('Error saving additional notes template:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in saveAdditionalNotesTemplate:', error);
    throw error;
  }
}

// Update existing additional notes template
export async function updateAdditionalNotesTemplate(templateData: {
  id: string;
  doctorId: string;
  disease: string;
  templateName: string;
  notes: string;
}) {
  try {
    const updateData = {
      doctor_id: templateData.doctorId,
      disease: templateData.disease,
      template_name: templateData.templateName,
      notes: templateData.notes,
      updated_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from('additional_notes_templates')
      .update(updateData)
      .eq('id', templateData.id);

    if (error) {
      console.error('Error updating additional notes template:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in updateAdditionalNotesTemplate:', error);
    throw error;
  }
}

// Load template notes
export async function loadAdditionalNotesTemplate(templateId: string): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('additional_notes_templates')
      .select('notes, usage_count')
      .eq('id', templateId)
      .single();

    if (error) {
      console.error('Error loading additional notes template:', error);
      throw error;
    }

    // Increment usage count using a separate query
    const newUsageCount = (data.usage_count || 0) + 1;
    await supabase
      .from('additional_notes_templates')
      .update({ 
        usage_count: newUsageCount,
        last_used: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', templateId);

    return data.notes;
  } catch (error) {
    console.error('Error in loadAdditionalNotesTemplate:', error);
    throw error;
  }
}

// Delete template
export async function deleteAdditionalNotesTemplate(templateId: string) {
  try {
    const { error } = await supabase
      .from('additional_notes_templates')
      .delete()
      .eq('id', templateId);

    if (error) {
      console.error('Error deleting additional notes template:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteAdditionalNotesTemplate:', error);
    throw error;
  }
}
